import * as qs from 'qs-esm'
import type { Payload, Where } from 'payload'

import { eventLocationSlugs, type EventLocationSlug } from '@/collections/Events/locationSlugs'
import type { Locale } from '@/lib/i18n/config'
import { getCanonicalLocationSlug } from '@/lib/locations/legacyLocationSlugs'
import type { Promotion } from '@/payload-types'

export const PROMOTIONS_PAGE_LIMIT = 100
export const BEST_DEAL_PROMOTIONS_LIMIT = 3
export const PROMOTIONS_LIST_SORT = '-publishedAt' as const

export type PromotionLocationFilter = 'all' | EventLocationSlug

export const PROMOTION_LIST_SELECT = {
  id: true,
  title: true,
  slug: true,
  shortDescription: true,
  heroImage: true,
  locations: true,
  isPermanent: true,
  isPromoted: true,
  showCountdown: true,
  expireDate: true,
  bookingDay: true,
  movingDay: true,
  minimumStay: true,
  meta: true,
  publishedAt: true,
} as const

export type PromotionListItem = Pick<
  Promotion,
  | 'id'
  | 'title'
  | 'slug'
  | 'shortDescription'
  | 'heroImage'
  | 'locations'
  | 'isPermanent'
  | 'isPromoted'
  | 'showCountdown'
  | 'expireDate'
  | 'bookingDay'
  | 'movingDay'
  | 'minimumStay'
  | 'meta'
  | 'publishedAt'
>

function getStartOfTodayISO(): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today.toISOString()
}

/** End of the expire calendar day (23:59:59.999 local time). */
export function getPromotionExpireEndOfDayMs(expireDate: string): number {
  const date = new Date(expireDate)

  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime()
}

type PromotionsWhereOptions = {
  excludeId?: number | string | null
  location: PromotionLocationFilter
}

export function buildPromotionsWhere({ excludeId, location }: PromotionsWhereOptions): Where {
  const conditions: Where[] = [
    { _status: { equals: 'published' } },
    {
      or: [
        { isPermanent: { equals: true } },
        { expireDate: { greater_than_equal: getStartOfTodayISO() } },
      ],
    },
  ]

  if (location !== 'all') {
    conditions.push({
      locations: { contains: location },
    })
  }

  if (excludeId != null) {
    conditions.push({ id: { not_equals: excludeId } })
  }

  return { and: conditions }
}

export function buildPromotionsWhereForLocation(location: PromotionLocationFilter): Where {
  return buildPromotionsWhere({ location })
}

type PromotionsQueryOptions = {
  excludeId?: number | string | null
  locale: Locale
  location: PromotionLocationFilter
  page: number
}

export type PromotionsPageResponse<T> = {
  docs: T[]
  hasNextPage: boolean
  page: number
  totalPages: number
}

export function buildPromotionsFindArgs({
  excludeId,
  locale,
  location,
  page,
}: PromotionsQueryOptions) {
  return {
    collection: 'promotions' as const,
    depth: 1,
    limit: PROMOTIONS_PAGE_LIMIT,
    locale,
    overrideAccess: false,
    page,
    pagination: true as const,
    select: PROMOTION_LIST_SELECT,
    sort: PROMOTIONS_LIST_SORT,
    where: buildPromotionsWhere({ excludeId, location }),
  }
}

function buildPromotedPromotionWhere(location: PromotionLocationFilter): Where {
  const baseWhere = buildPromotionsWhere({ location })
  const baseConditions = Array.isArray(baseWhere.and) ? baseWhere.and : [baseWhere]

  return {
    and: [...baseConditions, { isPromoted: { equals: true } }],
  }
}

export function buildFeaturedPromotionFindArgs({
  locale,
  location,
}: Pick<PromotionsQueryOptions, 'locale' | 'location'>) {
  return {
    collection: 'promotions' as const,
    depth: 1,
    limit: 1,
    locale,
    overrideAccess: false,
    pagination: false as const,
    select: PROMOTION_LIST_SELECT,
    sort: PROMOTIONS_LIST_SORT,
    where: buildPromotedPromotionWhere(location),
  }
}

export function buildPromotionsQueryParams(options: PromotionsQueryOptions) {
  return {
    depth: 1,
    limit: PROMOTIONS_PAGE_LIMIT,
    locale: options.locale,
    page: options.page,
    select: PROMOTION_LIST_SELECT,
    sort: PROMOTIONS_LIST_SORT,
    where: buildPromotionsWhere({
      excludeId: options.excludeId,
      location: options.location,
    }),
  }
}

export function buildFeaturedPromotionsQueryParams({
  locale,
  location,
}: Pick<PromotionsQueryOptions, 'locale' | 'location'>) {
  return {
    depth: 1,
    limit: 1,
    locale,
    select: PROMOTION_LIST_SELECT,
    sort: PROMOTIONS_LIST_SORT,
    where: buildPromotedPromotionWhere(location),
  }
}

export function buildPromotionsQueryString(options: PromotionsQueryOptions): string {
  return qs.stringify(buildPromotionsQueryParams(options), { addQueryPrefix: false })
}

export function buildFeaturedPromotionsQueryString(
  options: Pick<PromotionsQueryOptions, 'locale' | 'location'>,
): string {
  return qs.stringify(buildFeaturedPromotionsQueryParams(options), { addQueryPrefix: false })
}

export type PromotionLocationCounts = Record<PromotionLocationFilter, number>

const promotionLocationFilters: PromotionLocationFilter[] = ['all', ...eventLocationSlugs]

export async function getPromotionLocationCounts(
  payload: Payload,
  locale: Locale,
): Promise<PromotionLocationCounts> {
  const entries = await Promise.all(
    promotionLocationFilters.map(async (location) => {
      const { totalDocs } = await payload.count({
        collection: 'promotions',
        locale,
        overrideAccess: false,
        where: buildPromotionsWhere({ location }),
      })

      return [location, totalDocs ?? 0] as const
    }),
  )

  return Object.fromEntries(entries) as PromotionLocationCounts
}

/**
 * Unique canonical location slugs covered by published, active promotions.
 * Used by room-types inventory to match promotion-eligible listings.
 */
export async function findActivePromotionLocationSlugs(
  payload: Payload,
  locale: Locale,
  limit = 100,
): Promise<string[]> {
  const promotions = await findActivePromotions(payload, locale, 'all', limit)
  const slugs = new Set<string>()

  for (const promotion of promotions) {
    for (const location of promotion.locations ?? []) {
      if (typeof location === 'string' && location !== 'all') {
        slugs.add(getCanonicalLocationSlug(location))
      }
    }
  }

  return [...slugs]
}

export async function findActivePromotions(
  payload: Payload,
  locale: Locale,
  location: PromotionLocationFilter = 'all',
  limit = 100,
): Promise<PromotionListItem[]> {
  const result = await payload.find({
    collection: 'promotions',
    depth: 1,
    limit,
    locale,
    overrideAccess: false,
    pagination: false,
    select: PROMOTION_LIST_SELECT,
    sort: PROMOTIONS_LIST_SORT,
    where: buildPromotionsWhere({ location }),
  })

  return result.docs as PromotionListItem[]
}

export async function findFeaturedPromotion(
  payload: Payload,
  locale: Locale,
  location: PromotionLocationFilter = 'all',
): Promise<PromotionListItem | null> {
  const result = await payload.find(buildFeaturedPromotionFindArgs({ locale, location }))

  return (result.docs[0] as PromotionListItem | undefined) ?? null
}

export async function findPromotedPromotion(
  payload: Payload,
  locale: Locale,
  location: PromotionLocationFilter = 'all',
): Promise<PromotionListItem | null> {
  return findFeaturedPromotion(payload, locale, location)
}

export function selectRoomPromotion(
  promotedOrActive: PromotionListItem | null | PromotionListItem[],
  active?: PromotionListItem[],
): PromotionListItem[] {
  const initialList: PromotionListItem[] = []

  if (Array.isArray(promotedOrActive)) {
    initialList.push(...promotedOrActive)
  } else if (promotedOrActive) {
    initialList.push(promotedOrActive)
  }

  if (active) {
    initialList.push(...active)
  }

  const uniqueItems: PromotionListItem[] = []
  const seenIds = new Set<string | number>()

  for (const item of initialList) {
    if (!seenIds.has(item.id)) {
      uniqueItems.push(item)
      seenIds.add(item.id)
    }
  }

  const promotedOffers: PromotionListItem[] = []
  const otherOffers: PromotionListItem[] = []

  for (const item of uniqueItems) {
    if (item.isPromoted) {
      promotedOffers.push(item)
    } else {
      otherOffers.push(item)
    }
  }

  otherOffers.sort((left, right) => {
    const leftTime = left.publishedAt ? new Date(left.publishedAt).getTime() : 0
    const rightTime = right.publishedAt ? new Date(right.publishedAt).getTime() : 0
    return rightTime - leftTime
  })

  return [...promotedOffers, ...otherOffers]
}

/**
 * Returns every active promotion for a location. Any promoted offers
 * are placed first, followed by the rest without dropping any.
 */
export async function findRoomPromotion(
  payload: Payload,
  locale: Locale,
  location: PromotionLocationFilter,
): Promise<PromotionListItem[]> {
  const active = await findActivePromotions(payload, locale, location)

  return selectRoomPromotion(active)
}

export const findRoomPromotions = findRoomPromotion

export function buildPromotionSlugWhere(slug: string, draft = false): Where {
  if (draft) {
    return { slug: { equals: slug } }
  }

  const activeFilter = buildPromotionsWhere({ location: 'all' })
  const activeConditions = Array.isArray(activeFilter.and) ? activeFilter.and : [activeFilter]

  return {
    and: [{ slug: { equals: slug } }, ...activeConditions],
  }
}

export async function findPromotionBySlug(
  payload: Payload,
  {
    draft = false,
    locale,
    slug,
  }: {
    draft?: boolean
    locale: Locale
    slug: string
  },
): Promise<Promotion | null> {
  const result = await payload.find({
    collection: 'promotions',
    depth: 1,
    draft,
    limit: 1,
    locale,
    overrideAccess: draft,
    pagination: false,
    where: buildPromotionSlugWhere(slug, draft),
  })

  return result.docs?.[0] ?? null
}

export async function findPaginatedPromotions(
  payload: Payload,
  { excludeId, locale, location, page }: PromotionsQueryOptions,
): Promise<PromotionsPageResponse<PromotionListItem>> {
  const result = await payload.find(buildPromotionsFindArgs({ excludeId, locale, location, page }))

  return {
    docs: result.docs as PromotionListItem[],
    hasNextPage: result.hasNextPage ?? false,
    page: result.page ?? page,
    totalPages: result.totalPages ?? 1,
  }
}
